import { useCallback, useEffect, useState } from 'react';
import type { ReplicaStatus, Settings, StorageInfo } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { locale, t, tMessage } from '../lib/i18n';
import { Icon } from './Icon';
import { confirmDialog } from './Overlay';
import { toast } from './Toasts';

/**
 * Settings → Storage:
 * - Body cache on the server (one per user): cap, background prefetch of the last few days, clear;
 * - Local replica in desktop remote mode: how many days of message list to keep, how big the opened-message cache can get, clear and start over.
 */

function Row({ title, desc, children }: { title: string; desc?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="setting">
      <div className="setting__text">
        <div className="setting__title">{title}</div>
        {desc && <div className="setting__desc muted">{desc}</div>}
      </div>
      {children && <div className="setting__control">{children}</div>}
    </div>
  );
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(n < 10 * 1048576 ? 1 : 0)} MB`;
  return `${(n / 1073741824).toFixed(1)} GB`;
}

function Meter({ used, cap }: { used: number; cap: number }) {
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : used > 0 ? 100 : 0;
  return (
    <div className="meter" role="meter" aria-valuemin={0} aria-valuemax={cap} aria-valuenow={used} aria-label={t('已用空间')}>
      <i style={{ width: `${pct}%` }} className={pct > 90 ? 'is-full' : ''} />
    </div>
  );
}

const SERVER_CAPS = [0, 100, 300, 500, 1024, 2048, 5120, 10240];
const PREFETCH_DAYS = [0, 7, 30, 90, 365];
const REPLICA_DAYS = [30, 90, 180, 365, 730];
const REPLICA_CACHE = [0, 200, 500, 1024, 2048, 5120];

const mbLabel = (mb: number) => (mb === 0 ? t('不缓存') : mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`);
const dayLabel = (d: number) => (d === 0 ? t('不预下载') : d >= 365 ? t('最近 {n} 年', { n: d / 365 }) : t('最近 {n} 天', { n: d }));

export function StorageSettings({ settings, update }: { settings: Settings; update: (patch: Partial<Settings>) => Promise<void> }) {
  const [server, setServer] = useState<StorageInfo | null>(null);
  const [replica, setReplica] = useState<ReplicaStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [s, r] = await Promise.allSettled([api().storage.server(), api().storage.replica()]);
    if (s.status === 'fulfilled') {
      setServer(s.value);
      setError(null);
    } else setError(errorText(s.reason));
    if (r.status === 'fulfilled') setReplica(r.value);
  }, []);

  useEffect(() => {
    void refresh();
    // Prefetch and replica sync run in the background: refresh the numbers every few seconds while this page is open
    const timer = setInterval(() => void refresh(), 4000);
    return () => clearInterval(timer);
  }, [refresh]);

  const caps = server ? [...new Set([...SERVER_CAPS.filter((mb) => mb <= server.maxMb), server.maxMb])].sort((a, b) => a - b) : SERVER_CAPS;
  const capValue = settings.bodyCacheMb == null ? 'default' : String(settings.bodyCacheMb);

  const clearServer = async () => {
    const answer = await confirmDialog({
      title: t('清空服务器上的缓存？'),
      message: t('服务器上已经下载好的邮件正文会删掉（邮箱里的邮件不受影响），之后打开时再从邮箱重新下载。'),
      confirm: t('清空'),
      danger: true,
    });
    if (answer !== 'confirm') return;
    setBusy(true);
    try {
      setServer(await api().storage.clearServer());
      toast(t('服务器上的缓存清空了'), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const configureReplica = async (patch: { days?: number; cacheMb?: number }) => {
    try {
      setReplica(await api().storage.configureReplica(patch));
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const resetReplica = async () => {
    const answer = await confirmDialog({
      title: t('清空这台电脑上的副本？'),
      message: t('这台电脑上保存的邮件列表和打开过的邮件会删掉，然后从服务器重新同步。服务器上的数据不受影响。'),
      confirm: t('清空并重新同步'),
      danger: true,
    });
    if (answer !== 'confirm') return;
    try {
      setReplica(await api().storage.resetReplica());
      toast(t('正在从服务器重新同步'), 'info');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <div className="settings__section storage">
      <h3 className="settings__h">{t('服务器上的缓存')}</h3>
      <p className="muted settings__intro">{t('邮件一直保存在邮箱（Gmail、QQ 等）里。馍馍邮箱的服务器会把邮件正文下载一份存着，打开时就不用再等邮箱。')}</p>
      {error && <div className="notice notice--error">{error}</div>}
      {server && (
        <div className="storage__usage">
          <div className="storage__numbers">
            <b>{formatBytes(server.usedBytes)}</b>
            <span className="muted">
              / {server.capBytes > 0 ? formatBytes(server.capBytes) : t('不缓存')} · {t('已下载 {done} / {total} 封', { done: server.cachedMessages.toLocaleString(), total: server.totalMessages.toLocaleString() })}
            </span>
          </div>
          <Meter used={server.usedBytes} cap={server.capBytes} />
        </div>
      )}
      <Row title={t('缓存上限')} desc={t('超过时，最久没打开的邮件先删掉正文（列表和摘要还在，打开时重新下载）。')}>
        <select
          className="setting__select"
          value={capValue}
          onChange={(e) => void update({ bodyCacheMb: e.target.value === 'default' ? null : Number(e.target.value) }).then(refresh)}
          aria-label={t('缓存上限')}
        >
          <option value="default">{server ? t('默认（{size}）', { size: mbLabel(server.defaultMb) }) : t('默认')}</option>
          {caps.map((mb) => (
            <option key={mb} value={mb}>
              {mbLabel(mb)}
            </option>
          ))}
          {settings.bodyCacheMb != null && !caps.includes(settings.bodyCacheMb) && <option value={settings.bodyCacheMb}>{mbLabel(settings.bodyCacheMb)}</option>}
        </select>
      </Row>
      <Row title={t('提前下载')} desc={t('服务器在后台把这段时间的邮件正文先下好（未读的优先），打开就是秒开。缓存快满时会停下。')}>
        <select className="setting__select" value={settings.prefetchDays} onChange={(e) => void update({ prefetchDays: Number(e.target.value) }).then(refresh)} aria-label={t('提前下载')}>
          {[...new Set([...PREFETCH_DAYS, settings.prefetchDays])]
            .sort((a, b) => a - b)
            .map((d) => (
              <option key={d} value={d}>
                {dayLabel(d)}
              </option>
            ))}
        </select>
      </Row>
      <Row title={t('清空服务器缓存')} desc={t('腾出服务器空间，或者邮件显示不对时重新下载。')}>
        <button className="btn btn--small btn--quiet" onClick={() => void clearServer()} disabled={busy || !server || server.usedBytes === 0}>
          <Icon name="trash" size={15} /> {t('清空')}
        </button>
      </Row>

      {replica && (
        <>
          <h3 className="settings__h storage__h2">{t('这台电脑上的副本')}</h3>
          <p className="muted settings__intro">{t('连远程服务器时，这台电脑也存一份最近的邮件列表和打开过的邮件：启动和翻页不用等网络，断网时也能看。')}</p>
          <div className="storage__usage">
            <div className="storage__numbers">
              <b>{t('{n} 封', { n: replica.messages.toLocaleString() })}</b>
              <span className="muted">
                · {t('打开过的 {n} 封占 {size}', { n: replica.cachedThreads.toLocaleString(), size: formatBytes(replica.cacheBytes) })}
                {replica.cacheMb > 0 ? ` / ${mbLabel(replica.cacheMb)}` : ''}
                {replica.fileBytes != null ? t(' · 文件共 {size}', { size: formatBytes(replica.fileBytes) }) : ''}
              </span>
            </div>
            <Meter used={replica.cacheBytes} cap={replica.cacheMb * 1048576} />
            <div className="muted storage__sync">
              {replica.syncing ? (
                <>
                  <span className="spinner" /> {t('正在同步…')}
                </>
              ) : replica.error ? (
                <span className="storage__error">{t('同步失败：{error}', { error: tMessage(replica.error) })}</span>
              ) : replica.lastSync ? (
                t('上次同步 {time}', { time: new Date(replica.lastSync).toLocaleString(locale(), { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) })
              ) : (
                t('还没同步过')
              )}
            </div>
          </div>
          <Row title={t('保留多久的邮件列表')} desc={t('更早的邮件翻到时再问服务器。改长了要从服务器补齐。')}>
            <select className="setting__select" value={replica.days} onChange={(e) => void configureReplica({ days: Number(e.target.value) })} aria-label={t('保留多久')}>
              {[...new Set([...REPLICA_DAYS, replica.days])]
                .sort((a, b) => a - b)
                .map((d) => (
                  <option key={d} value={d}>
                    {d >= 365 ? t('{n} 年', { n: d / 365 }) : t('{n} 天', { n: d })}
                  </option>
                ))}
            </select>
          </Row>
          <Row title={t('打开过的邮件最多占')} desc={t('超过时最久没看的先删（再打开时从服务器取）。')}>
            <select className="setting__select" value={replica.cacheMb} onChange={(e) => void configureReplica({ cacheMb: Number(e.target.value) })} aria-label={t('本地缓存上限')}>
              {[...new Set([...REPLICA_CACHE, replica.cacheMb])]
                .sort((a, b) => a - b)
                .map((mb) => (
                  <option key={mb} value={mb}>
                    {mbLabel(mb)}
                  </option>
                ))}
            </select>
          </Row>
          <Row title={t('清空并重新同步')} desc={t('这台电脑上的副本出问题时用。')}>
            <button className="btn btn--small btn--quiet" onClick={() => void resetReplica()}>
              <Icon name="refresh" size={15} /> {t('重新同步')}
            </button>
          </Row>
        </>
      )}
    </div>
  );
}
